import React, { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import ReactDOM from "react-dom";
import { useFloatingPosition } from "../utils/useFloatingPosition";
import type { FloatingPlacement } from "../utils/floatingUtils";

export type PopoverControls = {
  isOpen: boolean;
  triggerRef: MutableRefObject<HTMLElement | null>;
  closePopover: () => void;
  togglePopover: () => void;
};

export type PopoverContentControls = PopoverControls & {
  contentRef: MutableRefObject<HTMLDivElement | null>;
};

type PopoverProps = {
  children: (controls: PopoverControls) => React.ReactNode;
  content: React.ReactNode | ((controls: PopoverContentControls) => React.ReactNode);
  defaultOpen?: boolean;
  placement?: FloatingPlacement;
  offset?: number;
  viewportPadding?: number;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  closeOnEscape?: boolean;
  "data-testid"?: string;
};

export const Popover: React.FC<PopoverProps> = ({
  children,
  content,
  defaultOpen = false,
  placement = "bottom-right",
  offset = 8,
  viewportPadding = 16,
  contentClassName,
  contentStyle,
  closeOnEscape = true,
  "data-testid": testId,
}) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const closePopover = useCallback(() => setIsOpen(false), []);
  const togglePopover = useCallback(() => setIsOpen((currentOpen) => !currentOpen), []);

  const { floatingStyle, currentPlacement } = useFloatingPosition({
    active: isOpen,
    anchorRef: triggerRef,
    floatingRef: contentRef,
    placement,
    offset,
    viewportPadding,
  });

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerEvent = (event: Event) => {
      if (!(event.target instanceof Node)) return;
      if (contentRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      closePopover();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === "Escape") closePopover();
    };

    document.addEventListener("mousedown", handlePointerEvent, true);
    document.addEventListener("touchstart", handlePointerEvent, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerEvent, true);
      document.removeEventListener("touchstart", handlePointerEvent, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [closeOnEscape, closePopover, isOpen]);

  const controls: PopoverControls = {
    isOpen,
    triggerRef,
    closePopover,
    togglePopover,
  };

  return (
    <>
      {children(controls)}
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={contentRef}
            className={contentClassName}
            style={{ ...floatingStyle, zIndex: 10001, ...contentStyle }}
            data-placement={currentPlacement}
            data-testid={testId}
          >
            {typeof content === "function"
              ? content({
                  ...controls,
                  contentRef,
                })
              : content}
          </div>,
          document.body
        )}
    </>
  );
};
